import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Paper,
  Typography,
  ThemeProvider,
  createTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import styled from '@emotion/styled';
import { 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  where, 
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  DOC_TYPES,
  QUOTE_STATUSES,
  LINE_TYPES,
  SIZES,
  sortSizes
} from '../firebase/schema';
import LineItem2 from '../components/LineItem2';
import AddCustomer from '../components/AddCustomer';
import { useFacility } from '../context/FacilityContext';
import { resolveFacilityId } from '../utils/facilities';
import FeesModal from '../components/FeesModal';
import { EMPLOYEE_ROLES } from '../constants/employeeRoles';
import SuccessAnimation from '../components/SuccessAnimation';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { computeSalesTax, sumTaxableFeesAmount } from '../utils/orderSalesTax';
import { enrichCustomerDocForCreate } from '../utils/customerPortal';
import { computeUpchargeQuantity } from '../utils/orderTotals';
import { duplicateLineItem } from '../utils/duplicateLineItem';
import { moveLineInArray } from '../utils/reorderLineItems';

// Generate unique IDs for line items
const generateUniqueId = (() => {
  let counter = 0;
  return (prefix = '') => {
    counter += 1;
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}-${counter}`;
  };
})();

// Theme (match NewOrder2)
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#98FB98' },
    background: { default: '#1C1C1C', paper: '#2C2C2C' },
    text: { primary: '#FFFFFF', secondary: 'rgba(255, 255, 255, 0.7)' }
  },
  typography: { fontFamily: '"Montserrat", sans-serif', button: { textTransform: 'uppercase', fontWeight: 600 } },
  components: {
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' }, styleOverrides: { root: { '& .MuiOutlinedInput-root': { backgroundColor: '#363636' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& input': { color: 'white' } } } },
    MuiSelect: { defaultProps: { size: 'small' }, styleOverrides: { root: { backgroundColor: '#363636', height: '40px' } } },
    MuiButton: { styleOverrides: { root: { borderRadius: 0, textTransform: 'uppercase', fontWeight: 600 }, contained: { backgroundColor: '#98FB98', color: '#000', '&:hover': { backgroundColor: '#7FE87F' } } } },
    MuiPaper: { styleOverrides: { root: { backgroundColor: '#2C2C2C', borderRadius: 0 } } }
  }
});

// Styled components (layout aligned with NewOrder2) – footer contained inside modal
const PageContainer = styled.div`
  text-transform: uppercase;
  background: var(--al-bg);
  height: 85vh;
  min-height: 400px;
  color: white;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  input, textarea { text-transform: uppercase; }
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
`;

const HeaderSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr;
  gap: 24px;
  padding: 24px;
  align-items: start;
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  background: var(--al-bg);
`;

const CustomerSearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const LinesSection = styled.div`
  padding: 16px 24px;
  margin-bottom: 16px;
`;

const FooterSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  flex-shrink: 0;
  background: var(--al-bg);
  padding: 16px 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
  @media (max-width: 768px) { flex-direction: column; gap: 8px; }
`;

const FooterBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 120px;
`;

const NotesFooterBox = styled(FooterBox)`
  flex: 2;
  min-width: 200px;
`;

const UpchargeFooterBox = styled(FooterBox)`
  flex: 1;
`;

const FeesFooterBox = styled(FooterBox)`
  flex: 1;

  .header {
    padding: 4px;
    font-weight: 600;
    font-size: 0.85em;
  }

  .summary {
    font-size: 0.8em;
    color: rgba(255, 255, 255, 0.6);
    flex: 1;
  }

  .fee-line {
    display: flex;
    justify-content: space-between;
    padding: 2px 0;
  }

  .fee-type {
    color: rgba(255, 255, 255, 0.8);
  }

  .fee-amount {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
  }

  .instructions {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .instruction-tag {
    background: ${props => props.theme.colors.primary};
    color: #000;
    padding: 2px 6px;
    font-size: 0.7em;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const EditFeesBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 16px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.75em;
  margin-top: auto;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const TotalsFooterBox = styled(FooterBox)`
  flex: 1;
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.2);

  .header {
    padding: 4px;
    font-weight: 700;
    font-size: 0.9em;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const FooterRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  .label {
    min-width: 45px;
    width: 45px;
    text-align: right;
    font-size: 0.85em;
    flex-shrink: 0;
    color: white;
    font-weight: 600;
  }

  input {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: ${props => props.theme.colors.primary};
    padding: 6px 8px;
    flex: 1;
    min-width: 60px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
  }
`;

const NotesBox = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 12px;
  margin: 0;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 0;
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8rem;
  resize: none;
  text-transform: uppercase;
  box-sizing: border-box;
  flex: 1;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FeeGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 180px 180px;
  gap: 12px;
  margin: 12px 0;
  padding: 8px;
`;

const FeeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: transparent;
  border-radius: 0;
  min-width: 0;
`;

const UpchargeColumn = styled(FeeColumn)`
  background: transparent;
`;

const FeeHeader = styled.div`
  margin-bottom: 4px;
  
  select {
    width: 100%;
    padding: 4px 8px;
    background: var(--al-primary);
    border: none;
    color: var(--al-bg);
    border-radius: 0;
    font-size: 0.8rem;
    font-family: 'Montserrat', sans-serif;
    text-transform: uppercase;
    
    &:focus {
      outline: none;
    }
  }
`;

const FeeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  background: #2D2D2D;
  border-radius: 0;
  
  label {
    width: 45px;
    color: white;
    font-size: 0.8rem;
    flex-shrink: 0;
  }
  
  input {
    width: 100px;
    padding: 4px 8px;
    background: #363636;
    border: 1px solid ${props => props.theme.colors.border};
    color: white;
    border-radius: 0;
    font-size: 0.8rem;
    font-family: 'Montserrat', sans-serif;
    text-transform: uppercase;
    
    /* Hide number spinners */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type=number] {
      -moz-appearance: textfield;
    }
    
    &:focus {
      outline: none;
      border-color: var(--al-primary);
    }
    
    &:disabled {
      background: #2D2D2D;
      cursor: not-allowed;
    }
  }
`;

const TotalsDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'Montserrat', sans-serif;
  margin-top: 20px;
`;

const PrimaryMetrics = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 24px;
  font-weight: 600;
`;

const SecondaryMetrics = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
`;

const TotalPieces = styled.span`
  color: white;
`;

const TotalAmount = styled.span`
  color: var(--al-primary);
`;

const CogsPercent = styled.span`
  color: white;
`;

const CreateOrderButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: var(--al-bg);
  border: none;
  padding: 8px 16px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  height: 32px;
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #363636;
  border: 1px solid ${props => props.theme.colors.border};
  border-top: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SearchResultItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  text-transform: uppercase;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
  }
`;

const AddNewButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: ${props => props.theme.colors.primary};
  color: #000000;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8rem;
  
  &:hover {
    opacity: 0.9;
  }
`;

// Helper functions
const generatePMKey = (pm, index) => {
  if (typeof pm === 'object' && pm.id) {
    return `pm-${pm.id}`;
  }
  if (typeof pm === 'object' && pm.name) {
    return `pm-name-${pm.name.replace(/\s+/g, '-').toLowerCase()}`;
  }
  if (typeof pm === 'string') {
    return `pm-str-${pm.replace(/\s+/g, '-').toLowerCase()}-${index}`;
  }
  return `pm-fallback-${index}`;
};

const deduplicatePMs = (pms) => {
  if (!Array.isArray(pms)) return [];
  const seen = new Set();
  return pms.filter(pm => {
    const value = typeof pm === 'object' ? pm.name : pm;
    const duplicate = seen.has(value);
    seen.add(value);
    return !duplicate;
  });
};

const hasActiveInstructions = (instructions) => {
  if (!instructions) return false;
  return instructions.shipping || instructions.sorting || instructions.folding ||
    instructions.bagging || instructions.fulfillment || instructions.neckTag ||
    instructions.taxExempt || instructions.designWorkOnly || instructions.doNotBill ||
    instructions.followUpEmail || instructions.instagramWorthy || instructions.providedGoods ||
    instructions.sample ||
    !!instructions.other;
};

// Map database line type to LINE_TYPES display value for form
const mapDbTypeToLineType = (dbType) => {
  const typeMap = {
    'screen': LINE_TYPES.SCREEN_PRINT,
    'embroidery': LINE_TYPES.EMBROIDERY,
    'dtg': LINE_TYPES.DTG,
    'dtf': LINE_TYPES.DTF,
    'design': LINE_TYPES.DESIGN_ONLY,
    'design_only': LINE_TYPES.DESIGN_ONLY
  };
  return typeMap[(dbType || '').toLowerCase()] || LINE_TYPES.SCREEN_PRINT;
};

// Normalize form line type to database format for quote lines
const normalizeQuoteLineType = (lineType) => {
  const t = (lineType != null ? String(lineType).trim() : '').toLowerCase();
  if (t.includes('screen')) return 'screen';
  if (t.includes('embroidery')) return 'embroidery';
  if (t === 'dtg') return 'dtg';
  if (t === 'dtf') return 'dtf';
  if (t.includes('design')) return 'design_only';
  return 'screen';
};

const defaultLine = () => ({
  id: generateUniqueId('line'),
  isExpanded: true,
  type: LINE_TYPES.SCREEN_PRINT,
  design: '',
  style: '',
  color: '',
  sizeType: 'ADULT',
  sizes: Object.fromEntries(SIZES.ADULT.map(size => [size, ''])),
  inkCounts: { Front: 1, Back: 0, Center: 0 },
  inks: {},
  frontInkColors: [],
  backInkColors: [],
  otherInkColors: [],
  printLocations: [],
  pricePerPiece: '',
  cogs: '',
  mockupUrl: ''
});

const NewQuote = ({ onClose, initialQuote, onSuccess }) => {
  const navigate = useNavigate();
  const { facilityId: activeFacilityId } = useFacility();
  const today = new Date();
  const defaultExpirationDate = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
  const isEditMode = Boolean(initialQuote?.id);

  // State variables
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [projectManagers, setProjectManagers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [lines, setLines] = useState([defaultLine()]);

  const [customers, setCustomers] = useState([]);
  const [quoteData, setQuoteData] = useState({
    customerName: '',
    projectManager: '',
    startDate: today.toISOString().split('T')[0],
    expirationDate: defaultExpirationDate.toISOString().split('T')[0],
    notes: '',
    fee1Type: '',
    fee1Quantity: '',
    fee1Price: '',
    fee2Type: '',
    fee2Quantity: '',
    fee2Price: '',
    upchargePrice: '2.00',
    probability: 0.5,
    revisionNumber: 1,
    discount: { type: 'fixed', amount: '' },
    additionalInstructions: {
      shipping: false,
      sorting: false,
      folding: false,
      bagging: false,
      fulfillment: false,
      neckTag: false,
      other: '',
      taxExempt: false,
      designWorkOnly: false,
      followUpEmail: false,
      doNotBill: false,
      instagramWorthy: false,
      providedGoods: false,
      sample: false
    },
    fees: []
  });
  const [showFeesModal, setShowFeesModal] = useState(false);

  const effectiveCustomerId = useMemo(() => {
    if (quoteData.customerId) return quoteData.customerId;
    const query = (customerSearch || quoteData.customerName || '').trim().toLowerCase();
    if (!query || !customers.length) return '';
    const match = customers.find((c) => (c.client || '').trim().toLowerCase() === query);
    return match?.id || '';
  }, [quoteData.customerId, quoteData.customerName, customerSearch, customers]);

  // Fetch customers on mount (match NewOrder2)
  useEffect(() => {
    const fetchCustomers = async () => {
      const q = query(collection(db, 'customers'), orderBy('client'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCustomers(list);
    };
    fetchCustomers();
  }, []);

  // Load initial quote when editing; reset form when opening for create
  useEffect(() => {
    if (!initialQuote?.id) {
      setQuoteData({
        customerName: '',
        projectManager: '',
        startDate: today.toISOString().split('T')[0],
        expirationDate: defaultExpirationDate.toISOString().split('T')[0],
        notes: '',
        fee1Type: '',
        fee1Quantity: '',
        fee1Price: '',
        fee2Type: '',
        fee2Quantity: '',
        fee2Price: '',
        upchargePrice: '2.00',
        probability: 0.5,
        revisionNumber: 1,
        discount: { type: 'fixed', amount: '' },
        additionalInstructions: {
          shipping: false,
          sorting: false,
          folding: false,
          bagging: false,
          fulfillment: false,
          neckTag: false,
          other: '',
          taxExempt: false,
          designWorkOnly: false,
          followUpEmail: false,
          doNotBill: false,
          instagramWorthy: false,
          providedGoods: false,
      sample: false
        },
        fees: []
      });
      setCustomerSearch('');
      setLines([defaultLine()]);
      return;
    }
    const q = initialQuote;
    const toDateStr = (val) => {
      if (!val) return '';
      const d = val && typeof val.toDate === 'function' ? val.toDate() : new Date(val);
      return d.toISOString ? d.toISOString().split('T')[0] : '';
    };
    const defaultInstructions = {
      shipping: false,
      sorting: false,
      folding: false,
      bagging: false,
      fulfillment: false,
      neckTag: false,
      other: '',
      taxExempt: false,
      designWorkOnly: false,
      followUpEmail: false,
      doNotBill: false,
      instagramWorthy: false,
      providedGoods: false,
      sample: false
    };
    setQuoteData({
      customerName: q.customerName || '',
      customerId: q.customerId || '',
      projectManager: q.projectManager || '',
      startDate: toDateStr(q.createdAt) || today.toISOString().split('T')[0],
      expirationDate: toDateStr(q.expiresAt) || defaultExpirationDate.toISOString().split('T')[0],
      notes: q.notes || '',
      fee1Type: q.fee1Type || '',
      fee1Quantity: q.fee1Quantity ?? '',
      fee1Price: q.fee1Price ?? '',
      fee2Type: q.fee2Type || '',
      fee2Quantity: q.fee2Quantity ?? '',
      fee2Price: q.fee2Price ?? '',
      upchargePrice: q.upchargePrice != null && q.upchargePrice !== '' ? String(q.upchargePrice) : '2.00',
      probability: q.probability ?? 0.5,
      revisionNumber: q.revisionNumber ?? 1,
      discount: q.discount && (q.discount.type || q.discount.amount !== undefined) ? { type: q.discount.type || 'fixed', amount: q.discount.amount ?? '' } : { type: 'fixed', amount: '' },
      additionalInstructions: q.additionalInstructions ? { ...defaultInstructions, ...q.additionalInstructions } : defaultInstructions,
      fees: (() => {
        if (Array.isArray(q.fees) && q.fees.length > 0) return q.fees;
        const built = [];
        if (q.fee1Type && (q.fee1Quantity || q.fee1Price)) {
          built.push({
            id: 1,
            type: q.fee1Type,
            quantity: q.fee1Quantity ?? '',
            price: q.fee1Price ?? '',
            taxable: true
          });
        }
        if (q.fee2Type && (q.fee2Quantity || q.fee2Price)) {
          built.push({
            id: 2,
            type: q.fee2Type,
            quantity: q.fee2Quantity ?? '',
            price: q.fee2Price ?? '',
            taxable: true
          });
        }
        return built;
      })()
    });
    setCustomerSearch(q.customerName || '');
    const mappedLines = (q.lines || [])
      .slice()
      .sort((a, b) => (Number(a.lineNumber) || 0) - (Number(b.lineNumber) || 0))
      .map((line) => ({
      id: line.id || generateUniqueId('line'),
      design: line.designName || line.design || '',
      style: line.garmentId || line.style || '',
      color: line.garmentColor || line.color || '',
      sizeType: line.sizeType || 'ADULT',
      sizes: line.sizes || Object.fromEntries(SIZES.ADULT.map(size => [size, ''])),
      pricePerPiece: line.pricePerPiece ?? line.price ?? '',
      price: line.pricePerPiece ?? line.price ?? '',
      cogs: line.cogs ?? '',
      frontInkColors: line.frontInkColors || [],
      backInkColors: line.backInkColors || [],
      otherInkColors: line.otherInkColors || [],
      printLocations: line.printLocations || [],
      designFileUrl: line.designFileUrl || null,
      type: mapDbTypeToLineType(line.type),
      inkCounts: { Front: 1, Back: 0, Center: 0 },
      inks: {},
      mockupUrl: ''
    }));
    setLines(mappedLines.length > 0 ? mappedLines : [defaultLine()]);
  }, [initialQuote?.id]);

  const calculateTotalQuantity = () => {
    if (!lines || !Array.isArray(lines)) return 0;
    
    return lines.reduce((total, line) => {
      if (!line || !line.sizes) return total;
      return total + Object.values(line.sizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    }, 0);
  };

  const calculate2XLPlus = () => {
    return lines.reduce((total, line) => total + computeUpchargeQuantity(line), 0);
  };

  const calculateTotalPieces = () => {
    if (!lines || !Array.isArray(lines)) return 0;
    
    return lines.reduce((total, line) => {
      if (!line || !line.sizes) return total;
      return total + Object.values(line.sizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    }, 0);
  };

  const calculatePricing = () => {
    if (!lines || !Array.isArray(lines)) {
      return {
        orderTotal: 0,
        cogsTotal: 0,
        cogsPercentage: 0,
        feesTotal: 0,
        discountAmount: 0,
        salesTax: 0
      };
    }

    const totals = lines.reduce((acc, line) => {
      if (!line || !line.sizes) return acc;

      const quantity = Object.values(line.sizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
      const price = Number(line.price) || Number(line.pricePerPiece) || 0;
      const cogs = Number(line.cogs) || 0;

      const linePrice = quantity * price;
      const lineCOGS = quantity * cogs;

      return {
        orderTotal: acc.orderTotal + linePrice,
        cogsTotal: acc.cogsTotal + lineCOGS
      };
    }, { orderTotal: 0, cogsTotal: 0 });

    const feesList = (() => {
      if (Array.isArray(quoteData.fees) && quoteData.fees.length > 0) {
        return quoteData.fees.filter((f) => f && f.type && (f.quantity !== '' || f.price !== ''));
      }
      const list = [];
      if (quoteData.fee1Type && (quoteData.fee1Quantity || quoteData.fee1Price)) {
        list.push({
          type: quoteData.fee1Type,
          quantity: quoteData.fee1Quantity,
          price: quoteData.fee1Price,
          taxable: true
        });
      }
      if (quoteData.fee2Type && (quoteData.fee2Quantity || quoteData.fee2Price)) {
        list.push({
          type: quoteData.fee2Type,
          quantity: quoteData.fee2Quantity,
          price: quoteData.fee2Price,
          taxable: true
        });
      }
      return list;
    })();

    const feesTotal = feesList.reduce(
      (sum, f) => sum + (Number(f.quantity) || 0) * (Number(f.price) || 0),
      0
    );
    const merchSubtotal = totals.orderTotal;
    const taxableFeesTotal = sumTaxableFeesAmount(feesList);
    const subtotalBeforeDiscount = merchSubtotal + feesTotal;

    let discountAmount = 0;
    if (quoteData.discount && quoteData.discount.amount !== '' && quoteData.discount.amount != null) {
      if (quoteData.discount.type === 'percent') {
        discountAmount = subtotalBeforeDiscount * (parseFloat(quoteData.discount.amount) / 100);
      } else {
        discountAmount = parseFloat(quoteData.discount.amount) || 0;
      }
    }
    const orderTotal = Math.max(0, subtotalBeforeDiscount - discountAmount);

    const salesTax = computeSalesTax({
      subtotal: merchSubtotal,
      feesTotal,
      taxableFeesTotal,
      discountAmount,
      isTaxExempt: Boolean(quoteData.additionalInstructions?.taxExempt)
    });

    return {
      orderTotal,
      cogsTotal: totals.cogsTotal,
      cogsPercentage: orderTotal > 0 ? (totals.cogsTotal / orderTotal) * 100 : 0,
      feesTotal,
      discountAmount,
      salesTax: Number(salesTax.toFixed(2))
    };
  };

  const handleFeesModalSave = (data) => {
    const fee1 = (data.fees && data.fees[0]) || {};
    const fee2 = (data.fees && data.fees[1]) || {};
    setQuoteData(prev => ({
      ...prev,
      fees: data.fees || [],
      fee1Type: fee1.type || '',
      fee1Quantity: fee1.quantity ?? '',
      fee1Price: fee1.price ?? '',
      fee2Type: fee2.type || '',
      fee2Quantity: fee2.quantity ?? '',
      fee2Price: fee2.price ?? '',
      discount: data.discount && (data.discount.type || data.discount.amount !== undefined) ? { type: data.discount.type || 'fixed', amount: data.discount.amount ?? '' } : { type: 'fixed', amount: '' },
      additionalInstructions: data.additionalInstructions || prev.additionalInstructions,
      ...('upchargePrice' in data && { upchargePrice: data.upchargePrice })
    }));
  };

  // Add useEffect for fetching project managers
  useEffect(() => {
    const fetchProjectManagers = async () => {
      try {
        const q = query(
          collection(db, 'employees'),
          where('role', '==', EMPLOYEE_ROLES.PROJECT_MANAGER)
        );
        
        const querySnapshot = await getDocs(q);
        const managersData = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(manager => manager.active)
          .sort((a, b) => a.name.localeCompare(b.name));
        
        setProjectManagers(managersData);
      } catch (error) {
        console.error('Error fetching project managers:', error);
      }
    };

    fetchProjectManagers();
  }, []);

  const handleCustomerSearch = (e) => {
    const query = e.target.value;
    setCustomerSearch(query);
    if (query.trim() === '') {
      setFilteredCustomers([]);
      return;
    }
    const filtered = customers.filter(c => (c.client || '').toLowerCase().includes(query.toLowerCase()));
    setFilteredCustomers(filtered);
  };

  const handleCustomerSelect = (customer) => {
    setQuoteData(prev => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.client
    }));
    setCustomerSearch(customer.client);
    setFilteredCustomers([]);
  };

  const handleCustomerSearchBlur = () => {
    if (quoteData.customerId) return;
    const query = customerSearch.trim().toLowerCase();
    if (!query) return;
    const match = customers.find((c) => (c.client || '').trim().toLowerCase() === query);
    if (match) handleCustomerSelect(match);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If there's exactly one filtered customer, select it
      if (filteredCustomers.length === 1) {
        handleCustomerSelect(filteredCustomers[0]);
      }
    }

    // Close dropdown on Escape
    if (e.key === 'Escape') {
      setFilteredCustomers([]);
    }
  };

  // Line item handlers
  const handleAddLine = () => {
    setLines((prevLines) => [
      ...prevLines,
      {
        ...defaultLine(),
        lineNumber: prevLines.length + 1
      }
    ]);
  };

  const handleMoveLine = useCallback((index, delta) => {
    setLines((prev) => moveLineInArray(prev, index, delta));
  }, []);

  const handleDuplicateLine = useCallback((index) => {
    setLines((prev) => {
      const source = prev[index];
      if (!source) return prev;
      const duplicate = duplicateLineItem(source, {
        lineNumber: prev.length + 1,
        isExpanded: true
      });
      const next = [...prev];
      next.splice(index + 1, 0, duplicate);
      return next.map((line, i) => ({ ...line, lineNumber: i + 1 }));
    });
  }, []);

  const handleLineUpdate = (index, updatedLine) => {
    setLines(prevLines => {
      // Prevent unnecessary updates
      if (JSON.stringify(prevLines[index]) === JSON.stringify(updatedLine)) {
        return prevLines;
      }
      
      const newLines = [...prevLines];
      newLines[index] = {
        ...newLines[index],
        ...updatedLine,
        id: newLines[index].id, // Preserve the original ID
        price: updatedLine.price || updatedLine.pricePerPiece || newLines[index].price || 0,
        cogs: updatedLine.cogs || newLines[index].cogs || 0
      };
      return newLines;
    });
  };

  const handleRemoveLine = (index) => {
    setLines(prevLines => prevLines.filter((_, i) => i !== index));
  };

  const handleAddNewCustomer = async (customerData) => {
    try {
      const customersRef = collection(db, 'customers');
      const clientName = (customerData.client || '').toUpperCase();
      const docRef = await addDoc(customersRef, enrichCustomerDocForCreate({
        ...customerData,
        client: clientName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }));
      const newCustomer = { id: docRef.id, ...customerData, client: clientName };
      setCustomers(prev => [...prev, newCustomer]);
      setQuoteData(prev => ({ ...prev, customerId: docRef.id, customerName: clientName }));
      setCustomerSearch(clientName);
      setFilteredCustomers([]);
      setShowAddCustomer(false);
    } catch (error) {
      console.error('Error adding new customer:', error);
      alert('Error adding customer: ' + error.message);
    }
  };

  const handleSave = async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);

      const quoteNumber = isEditMode
        ? initialQuote.quoteNumber
        : `Q${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

      console.log(isEditMode ? 'Updating quote' : 'Creating quote', { quoteData, lines });

      // Upload artwork files and update line items
      const updatedLines = await Promise.all(lines.map(async (line) => {
        if (line.artworkFile) {
          const storageRef = ref(storage, `artwork/quotes/${quoteNumber}/${line.artworkFile.name}`);
          await uploadBytes(storageRef, line.artworkFile);
          const url = await getDownloadURL(storageRef);
          return { ...line, designFileUrl: url };
        }
        return line;
      }));

      const pricing = calculatePricing();
      const totalQty = calculateTotalQuantity();

      const quotePayload = {
        type: DOC_TYPES.QUOTE,
        quoteNumber,
        projectManager: quoteData.projectManager,
        customerId: quoteData.customerId,
        customerName: quoteData.customerName,
        updatedAt: serverTimestamp(),
        expiresAt: new Date(quoteData.expirationDate),
        notes: quoteData.notes || '',
        status: QUOTE_STATUSES.DRAFT,
        probability: quoteData.probability,
        revisionNumber: quoteData.revisionNumber,
        fee1Type: quoteData.fee1Type || '',
        fee1Quantity: parseFloat(quoteData.fee1Quantity) || 0,
        fee1Price: parseFloat(quoteData.fee1Price) || 0,
        fee2Type: quoteData.fee2Type || '',
        fee2Quantity: parseFloat(quoteData.fee2Quantity) || 0,
        fee2Price: parseFloat(quoteData.fee2Price) || 0,
        ...(Array.isArray(quoteData.fees) && quoteData.fees.length > 0 && {
          fees: quoteData.fees
            .filter((f) => f && f.type)
            .map((f, index) => ({
              id: f.id || `fee-${index + 1}`,
              type: String(f.type),
              quantity: Number(f.quantity) || 0,
              price: Number(f.price) || 0,
              taxable: f.taxable !== false
            }))
        }),
        totalQuantity: totalQty,
        subtotal: pricing.orderTotal,
        cogsTotal: pricing.cogsTotal,
        cogsPercentage: pricing.cogsPercentage,
        extraFees: pricing.feesTotal,
        totalLines: updatedLines.length,
        discount: quoteData.discount || null,
        additionalInstructions: quoteData.additionalInstructions || null
      };

      let quoteId;
      if (isEditMode) {
        quoteId = initialQuote.id;
        await updateDoc(doc(db, 'quotes', quoteId), quotePayload);
        // Delete existing quote lines so we can replace with current form data
        const linesRef = collection(db, 'quoteLines');
        const q = query(linesRef, where('quoteId', '==', quoteId));
        const existingSnapshot = await getDocs(q);
        await Promise.all(existingSnapshot.docs.map((lineDoc) => deleteDoc(doc(db, 'quoteLines', lineDoc.id))));
      } else {
        const createPayload = {
          ...quotePayload,
          facilityId: resolveFacilityId(activeFacilityId),
          createdAt: serverTimestamp()
        };
        const quoteRef = await addDoc(collection(db, 'quotes'), createPayload);
        quoteId = quoteRef.id;
      }

      // Create line items as separate documents
      const linePromises = updatedLines.map(async (line, index) => {
        const processedSizes = {};
        if (line.sizes) {
          Object.entries(line.sizes).forEach(([size, quantity]) => {
            processedSizes[size] = Number(quantity) || 0;
          });
        }
        const frontInkColors = Array.isArray(line.frontInkColors) ? line.frontInkColors.filter(color => color) : [];
        const backInkColors = Array.isArray(line.backInkColors) ? line.backInkColors.filter(color => color) : [];
        const otherInkColors = Array.isArray(line.otherInkColors) ? line.otherInkColors.filter(color => color) : [];
        const totalQuantity = Object.values(processedSizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
        const pricePerPiece = Number(line.price) || 0;
        const cogs = Number(line.cogs) || 0;

        const lineItem = {
          quoteId,
          lineNumber: index + 1,
          designName: line.design || '',
          garmentId: line.style || '',
          garmentColor: line.color || '',
          sizes: processedSizes,
          pricePerPiece,
          cogs,
          status: QUOTE_STATUSES.DRAFT,
          frontInkColors,
          backInkColors,
          otherInkColors,
          printLocations: line.printLocations || [],
          sizeType: line.sizeType || 'unisex',
          totalQuantity,
          totalLinePrice: totalQuantity * pricePerPiece,
          totalLineCOGS: totalQuantity * cogs,
          type: normalizeQuoteLineType(line.type),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          designFileUrl: line.designFileUrl || null
        };

        return addDoc(collection(db, 'quoteLines'), lineItem);
      });

      const lineResults = await Promise.all(linePromises);
      await updateDoc(doc(db, 'quotes', quoteId), {
        lineRefs: lineResults.map(ref => ref.id)
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setIsSubmitting(false);
        if (typeof onSuccess === 'function') {
          onSuccess();
        } else {
          window.location.href = '/quotes';
        }
      }, 2000);
    } catch (error) {
      console.error('Error saving quote:', error);
      alert('Error saving quote: ' + error.message);
      setIsSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
    <PageContainer>
      <ScrollableContent>
        <HeaderSection>
          <CustomerSearchContainer>
            <TextField
              fullWidth
              label="Search Customers"
              value={customerSearch}
              onChange={handleCustomerSearch}
              onBlur={handleCustomerSearchBlur}
              onKeyDown={handleKeyDown}
              InputProps={{ sx: { textTransform: 'uppercase' } }}
            />
            {filteredCustomers.length > 0 && (
              <SearchResults>
                {filteredCustomers.map(customer => (
                  <SearchResultItem key={customer.id} onClick={() => handleCustomerSelect(customer)}>
                  {customer.client}
                </SearchResultItem>
                ))}
              </SearchResults>
            )}
            {customerSearch && !filteredCustomers.length && !quoteData.customerName && (
              <SearchResults>
                <SearchResultItem onClick={() => setShowAddCustomer(true)} style={{ color: 'var(--al-primary)' }}>
                  + Add New Customer
                </SearchResultItem>
              </SearchResults>
            )}
          </CustomerSearchContainer>

          <FormControl fullWidth size="small">
            <InputLabel>Project Manager</InputLabel>
            <Select
              value={quoteData.projectManager}
              onChange={(e) => setQuoteData(prev => ({ ...prev, projectManager: e.target.value }))}
              label="Project Manager"
            >
              <MenuItem value=""><em>Select PM</em></MenuItem>
              {Array.isArray(projectManagers) && deduplicatePMs(projectManagers).map((pm, index) => {
                const key = generatePMKey(pm, index);
                const value = typeof pm === 'object' ? pm.name : pm;
                return <MenuItem key={key} value={value}>{value}</MenuItem>;
              })}
            </Select>
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={quoteData.startDate}
                onChange={(e) => setQuoteData(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Expiration Date"
                type="date"
                value={quoteData.expirationDate}
                onChange={(e) => setQuoteData(prev => ({ ...prev, expirationDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </HeaderSection>

        <LinesSection>
          {lines.map((line, index) => (
            <Box key={line.id} sx={{ mb: 2 }}>
              <LineItem2
                lineData={line}
                index={index}
                customerId={effectiveCustomerId}
                onUpdate={(updatedLine) => handleLineUpdate(index, updatedLine)}
                onDelete={() => handleRemoveLine(index)}
                onDuplicate={() => handleDuplicateLine(index)}
                onMoveUp={() => handleMoveLine(index, -1)}
                onMoveDown={() => handleMoveLine(index, 1)}
                canMoveUp={index > 0}
                canMoveDown={index < lines.length - 1}
              />
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" onClick={handleAddLine} startIcon={<AddIcon />}>
              Add Line
            </Button>
          </Box>
        </LinesSection>
      </ScrollableContent>

      <FooterSection>
        <NotesFooterBox>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Quote Notes..."
            value={quoteData.notes}
            onChange={(e) => setQuoteData(prev => ({ ...prev, notes: e.target.value }))}
            InputProps={{ sx: { textTransform: 'uppercase', height: '100%', background: 'transparent', '& textarea': { height: '100% !important' } } }}
            sx={{ flex: 1 }}
          />
        </NotesFooterBox>

        <UpchargeFooterBox>
          <div style={{ padding: '4px', fontWeight: 600, fontSize: '0.85em' }}>+2XL Upcharge</div>
          <FooterRow>
            <span className="label">QTY:</span>
            <input type="text" value={calculate2XLPlus() || 0} readOnly style={{ backgroundColor: 'rgba(54, 54, 54, 0.5)' }} />
          </FooterRow>
          <FooterRow>
            <span className="label">$:</span>
            <input
              type="text"
              value={quoteData.upchargePrice === '' || quoteData.upchargePrice == null ? '2.00' : quoteData.upchargePrice}
              onChange={(e) => setQuoteData(prev => ({ ...prev, upchargePrice: e.target.value }))}
            />
          </FooterRow>
          <FooterRow>
            <span className="label">Total:</span>
            <input
              type="text"
              value={((calculate2XLPlus() || 0) * (parseFloat(quoteData.upchargePrice) || 0)).toFixed(2)}
              readOnly
              style={{ backgroundColor: 'rgba(54, 54, 54, 0.5)' }}
            />
          </FooterRow>
        </UpchargeFooterBox>

        {/* Fees, Discounts & Instructions Column */}
        <FeesFooterBox>
          <div className="header">Fees, Discounts & Instructions</div>
          <div className="summary">
            {quoteData.fee1Type && (quoteData.fee1Quantity || quoteData.fee1Price) && (
              <div className="fee-line" key="fee1">
                <span className="fee-type">{quoteData.fee1Type}</span>
                <span className="fee-amount">${((Number(quoteData.fee1Quantity) || 0) * (Number(quoteData.fee1Price) || 0)).toFixed(2)}</span>
              </div>
            )}
            {quoteData.fee2Type && (quoteData.fee2Quantity || quoteData.fee2Price) && (
              <div className="fee-line" key="fee2">
                <span className="fee-type">{quoteData.fee2Type}</span>
                <span className="fee-amount">${((Number(quoteData.fee2Quantity) || 0) * (Number(quoteData.fee2Price) || 0)).toFixed(2)}</span>
              </div>
            )}
            {quoteData.discount && quoteData.discount.amount !== '' && quoteData.discount.amount != null && (
              <div className="fee-line">
                <span className="fee-type">Discount ({quoteData.discount.type === 'percent' ? `${quoteData.discount.amount}%` : 'Fixed'})</span>
                <span className="fee-amount" style={{ color: '#FF6B6B' }}>
                  -${calculatePricing().discountAmount.toFixed(2)}
                </span>
              </div>
            )}
            {hasActiveInstructions(quoteData.additionalInstructions) && (
              <div className="instructions">
                {quoteData.additionalInstructions.shipping && <span className="instruction-tag">Shipping</span>}
                {quoteData.additionalInstructions.sorting && <span className="instruction-tag">Sorting</span>}
                {quoteData.additionalInstructions.folding && <span className="instruction-tag">Folding</span>}
                {quoteData.additionalInstructions.bagging && <span className="instruction-tag">Bagging</span>}
                {quoteData.additionalInstructions.fulfillment && <span className="instruction-tag">Fulfillment</span>}
                {quoteData.additionalInstructions.neckTag && <span className="instruction-tag">Neck Tag</span>}
                {quoteData.additionalInstructions.taxExempt && <span className="instruction-tag" style={{ background: '#2196F3' }}>Tax Exempt</span>}
                {quoteData.additionalInstructions.designWorkOnly && <span className="instruction-tag" style={{ background: '#9C27B0' }}>Design Only</span>}
                {quoteData.additionalInstructions.doNotBill && <span className="instruction-tag" style={{ background: '#f44336' }}>Do Not Bill</span>}
                {quoteData.additionalInstructions.followUpEmail && <span className="instruction-tag" style={{ background: '#2196F3' }}>Follow-Up Email</span>}
                {quoteData.additionalInstructions.instagramWorthy && <span className="instruction-tag">Instagram Worthy</span>}
                {quoteData.additionalInstructions.providedGoods && <span className="instruction-tag" style={{ background: '#5B9BD5' }}>Provided Goods</span>}
                {quoteData.additionalInstructions.sample && <span className="instruction-tag" style={{ background: '#8E6BBE' }}>Sample</span>}
                {quoteData.additionalInstructions.other && <span className="instruction-tag" style={{ background: '#607D8B' }}>{quoteData.additionalInstructions.other}</span>}
              </div>
            )}
            {!(quoteData.fee1Type && (quoteData.fee1Quantity || quoteData.fee1Price)) &&
             !(quoteData.fee2Type && (quoteData.fee2Quantity || quoteData.fee2Price)) &&
             (!quoteData.discount || quoteData.discount.amount === '' || quoteData.discount.amount == null) &&
             !hasActiveInstructions(quoteData.additionalInstructions) && (
              <span style={{ fontStyle: 'italic' }}>No fees, discounts, or instructions</span>
            )}
          </div>
          <EditFeesBtn onClick={() => setShowFeesModal(true)}>
            Edit
          </EditFeesBtn>
        </FeesFooterBox>

        {/* Totals Column */}
        <TotalsFooterBox>
          <div className="header">TOTALS</div>
          <FooterRow>
            <span className="label">PCS:</span>
            <input type="text" value={calculateTotalPieces()} readOnly />
          </FooterRow>
          <FooterRow>
            <span className="label">$:</span>
            <input
              type="text"
              value={(() => {
                const pricing = calculatePricing();
                const orderTotal = pricing.orderTotal;
                const taxExempt = quoteData.additionalInstructions?.taxExempt;
                const tax = taxExempt ? 0 : pricing.salesTax;
                return tax > 0 ? `$${orderTotal.toFixed(2)} + $${tax.toFixed(2)}/tax` : `$${orderTotal.toFixed(2)}`;
              })()}
              readOnly
            />
          </FooterRow>
          <FooterRow>
            <span className="label">Stats:</span>
            <input
              type="text"
              value={`${calculatePricing().cogsPercentage.toFixed(1)}% COGS`}
              readOnly
            />
          </FooterRow>
          <Button
            variant="contained"
            fullWidth
            size="small"
            onClick={handleSave}
            disabled={isSubmitting}
            sx={{ mt: 'auto' }}
          >
            {isSubmitting ? (initialQuote?.id ? 'Saving...' : 'Creating...') : (initialQuote?.id ? 'Update Quote' : 'Create Quote')}
          </Button>
        </TotalsFooterBox>
      </FooterSection>

      {showSuccess && <SuccessAnimation onComplete={() => setShowSuccess(false)} />}

      <AddCustomer
        isOpen={showAddCustomer}
        onClose={() => setShowAddCustomer(false)}
        onAdd={handleAddNewCustomer}
        initialData={{ client: customerSearch }}
      />

      <FeesModal
        isOpen={showFeesModal}
        onClose={() => setShowFeesModal(false)}
        onSave={handleFeesModalSave}
        initialData={{
          fees: Array.isArray(quoteData.fees) && quoteData.fees.length > 0
            ? quoteData.fees
            : [
                { id: 1, type: quoteData.fee1Type || '', quantity: quoteData.fee1Quantity || '', price: quoteData.fee1Price || '' },
                { id: 2, type: quoteData.fee2Type || '', quantity: quoteData.fee2Quantity || '', price: quoteData.fee2Price || '' }
              ],
          discount: quoteData.discount || { type: 'fixed', amount: '' },
          additionalInstructions: quoteData.additionalInstructions || {},
          upchargePrice: quoteData.upchargePrice
        }}
      />
    </PageContainer>
    </ThemeProvider>
  );
};

export default NewQuote; 